import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Collapse, Table, Tag, Empty, Spin, Typography, Button, Modal, Input, Space, message } from 'antd';
import { ShoppingOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import {
  getShopifyOrders,
  getUserShopifyOrders,
  linkShopifyOrder,
  unlinkShopifyOrder,
} from '@app/api/admin.api';
import { MATCH_COLORS, MATCH_LABELS, ShopifyOrderModel } from '@app/models/ShopifyOrderModel';

const { Panel } = Collapse;
const { Text } = Typography;

const PANEL_KEY = 'shopify';
// Matches the translucent panels used by HealthDataSection and the usage chart
// in this modal, so the section doesn't look bolted on.
const PANEL_BG: React.CSSProperties = {
  background: 'var(--soft-panel-bg)',
  border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)',
  borderRadius: 8,
  padding: 12,
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

interface Props {
  userId: number;
  refreshNonce?: number;
}

const ShopifyOrdersSection: React.FC<Props> = ({ userId, refreshNonce = 0 }) => {
  const [orders, setOrders] = useState<ShopifyOrderModel[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [linkOpen, setLinkOpen] = useState(false);
  const [lookup, setLookup] = useState('');
  const [candidates, setCandidates] = useState<ShopifyOrderModel[]>([]);
  const [searching, setSearching] = useState(false);
  const [linkingId, setLinkingId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getUserShopifyOrders(userId)
      .then((data) => setOrders(data.results || []))
      .catch(() => setOrders([]))
      .finally(() => {
        setLoading(false);
        setLoaded(true);
      });
  }, [userId]);

  // Fetch on first expand only, so opening the popup stays cheap.
  const onChange = (keys: string | string[]) => {
    const open = Array.isArray(keys) ? keys.includes(PANEL_KEY) : keys === PANEL_KEY;
    if (open && !loaded) load();
  };

  // The modal's Refresh button bumps refreshNonce — re-fetch if this section was
  // already opened (even if since collapsed). Skips initial mount / unopened.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    if (loaded && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const searchOrders = () => {
    if (!lookup.trim()) return;
    setSearching(true);
    getShopifyOrders({ search: lookup.trim() })
      .then((data) => setCandidates(data.results || []))
      .catch(() => setCandidates([]))
      .finally(() => setSearching(false));
  };

  const doLink = (order: ShopifyOrderModel) => {
    setLinkingId(order.id);
    linkShopifyOrder(order.id, userId)
      .then(() => {
        message.success(`Order ${order.orderNumber} linked.`);
        setLinkOpen(false);
        setLookup('');
        setCandidates([]);
        load();
      })
      .catch((e) => {
        // 409 means the order is serial-matched and deliberately immutable.
        const detail = e?.response?.data?.detail;
        message.error(detail || 'Could not link that order.');
      })
      .finally(() => setLinkingId(null));
  };

  const doUnlink = (order: ShopifyOrderModel) => {
    Modal.confirm({
      title: `Unlink order ${order.orderNumber}?`,
      content: 'This removes the connection between the order and this user.',
      okText: 'Unlink',
      okButtonProps: { danger: true },
      onOk: () =>
        unlinkShopifyOrder(order.id)
          .then(() => {
            message.success('Order unlinked.');
            load();
          })
          .catch((e) => message.error(e?.response?.data?.detail || 'Could not unlink.')),
    });
  };

  const columns: ColumnsType<ShopifyOrderModel> = [
    { title: 'Order', dataIndex: 'orderNumber', width: 100 },
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      width: 170,
      render: (s: string) =>
        s ? (
          <Text
            code
            title={s}
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                     display: 'block', maxWidth: '100%' }}
          >
            {s}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    { title: 'Product', dataIndex: 'product', width: 260, ellipsis: true },
    { title: 'Ordered', dataIndex: 'orderedAt', width: 120, render: fmtDate },
    {
      title: 'Status',
      dataIndex: 'fulfillmentStatus',
      width: 120,
      render: (s: string) => (s ? <Tag>{s}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Matched',
      dataIndex: 'matchedBy',
      width: 110,
      render: (_: string, row) => (
        <Tag color={MATCH_COLORS[row.matchedBy]} title={row.matchedByLabel} style={{ margin: 0 }}>
          {MATCH_LABELS[row.matchedBy] || row.matchedByLabel}
        </Tag>
      ),
    },
    {
      title: '',
      width: 50,
      render: (_: unknown, row) =>
        row.isLocked ? null : (
          <Button
            size="small"
            type="text"
            danger
            icon={<DisconnectOutlined />}
            title="Unlink this order"
            onClick={() => doUnlink(row)}
          />
        ),
    },
  ];

  return (
    <>
      {/* Same shell as HealthDataSection: tinted icon tile on the left, title
          and subtitle, chevron on the right, wrapped in the shared panel. */}
      <div style={{ ...PANEL_BG, padding: 0, overflow: 'hidden' }}>
        <Collapse ghost onChange={onChange} expandIconPosition="right">
          <Panel
            key={PANEL_KEY}
            // The button lives inside `header` rather than in `extra`: AntD
            // floats `extra` right, and a float pins itself to the first line
            // box, so against this two-line header it never centres. A flex row
            // with margin-left:auto centres it against the full header height.
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10, display: 'flex', flexShrink: 0,
                  alignItems: 'center', justifyContent: 'center', background: '#52c41a22',
                }}>
                  <ShoppingOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>
                    Shopify Orders{loaded ? ` (${orders.length})` : ''}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Order numbers, cushion serials &amp; purchase dates — tap to expand
                  </Text>
                </div>
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  style={{ marginLeft: 'auto', flexShrink: 0 }}
                  // Inside the header, a click would otherwise toggle the panel.
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkOpen(true);
                  }}
                >
                  Link an order
                </Button>
              </div>
            }
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : orders.length ? (
              <Table
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={orders}
                pagination={false}
                scroll={{ x: 900 }}
              />
            ) : (
              <Empty description="No Shopify orders linked to this user" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Panel>
        </Collapse>
      </div>

      <Modal
        title="Link an order to this user"
        visible={linkOpen}
        onCancel={() => setLinkOpen(false)}
        footer={null}
        width={720}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            Search by order number, serial number, or the email used at checkout.
          </Text>
          <Input.Search
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            onSearch={searchOrders}
            enterButton="Search"
            loading={searching}
            placeholder="e.g. #10166 or E222200538"
            allowClear
          />
          {candidates.length > 0 && (
            <Table
              rowKey="id"
              size="small"
              dataSource={candidates}
              pagination={false}
              scroll={{ y: 320 }}
              columns={[
                { title: 'Order', dataIndex: 'orderNumber', width: 100 },
                { title: 'Serial', dataIndex: 'serialNumber', width: 170 },
                { title: 'Product', dataIndex: 'product', ellipsis: true },
                {
                  title: 'Currently',
                  width: 190,
                  render: (_: unknown, row: ShopifyOrderModel) =>
                    row.user ? (
                      <Text type="secondary" ellipsis>
                        {row.user.email}
                      </Text>
                    ) : (
                      <Tag>Unlinked</Tag>
                    ),
                },
                {
                  title: '',
                  width: 90,
                  render: (_: unknown, row: ShopifyOrderModel) =>
                    row.isLocked ? (
                      <Text type="secondary" style={{ fontSize: 12 }} title="Verified by cushion pairing">
                        Locked
                      </Text>
                    ) : (
                      <Button
                        size="small"
                        type="primary"
                        loading={linkingId === row.id}
                        onClick={() => doLink(row)}
                      >
                        Link
                      </Button>
                    ),
                },
              ]}
            />
          )}
          {!searching && lookup && candidates.length === 0 && (
            <Empty description="No matching orders" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Space>
      </Modal>
    </>
  );
};

export default ShopifyOrdersSection;
